if (!customElements.get('cart-recommendations')) {
  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.cartUpdateUnsubscriber = undefined;
      this.isInitialized = false;
      this.lastCartProductIds = null;

      this.init()
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Reinitialize on any cart update (item add, remove, or quantity change)
        this.reinitialize();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    reinitialize() {
      this.init();
    }

    async init() {
      // Get fresh cart data when reinitializing
      const cartProductIds = await this.#getCurrentCartProductIds();

      // Check if cart has actually changed to avoid unnecessary re-renders
      const cartChanged = !this.lastCartProductIds ||
        this.lastCartProductIds.length !== cartProductIds.length ||
        !this.lastCartProductIds.every(id => cartProductIds.includes(id));

      if (!cartChanged && this.isInitialized) {
        return;
      }

      this.lastCartProductIds = [...cartProductIds];

      if (cartProductIds.length === 0) {
        this.showEmpty();
        return;
      }

      try {
        await this.renderRecommendations(cartProductIds);
      } catch (error) {
        this.showEmpty();
      } finally {
        this.isInitialized = true;
      }
    }

    async #getCurrentCartProductIds() {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          const cart = await response.json();
          return cart.items.map(item => item.product_id.toString());
        }
      } catch (error) {
        console.warn('Failed to fetch current cart data:', error);
      }

      // Fallback to initial dataset if cart fetch fails
      return this.dataset.cartProductIds?.split(',').filter(Boolean) || [];
    }

    async renderRecommendations(cartProductIds) {
      const sectionId = this.dataset.sectionId;
      const limit = parseInt(this.dataset.limit) || 4;
      const intent = this.dataset.intent || 'related';

      const uniqueCartProductIds = [...new Set(cartProductIds)];

      try {
        const allProductCards = [];
        const seenProductHandles = new Set();

        for (const productId of uniqueCartProductIds) {
          try {
            const url = `${window.Shopify.routes.root}recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=${intent}`;
            const response = await fetch(url);

            if (response.ok) {
              const html = await response.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newSection = doc.querySelector('cart-recommendations');

              if (newSection) {
                const productCards = newSection.querySelectorAll('.grid-item');

                productCards.forEach(card => {
                  const productLink = card.querySelector('a[href*="/products/"]');
                  if (productLink) {
                    const productHandle = productLink.href.split('/products/')[1]?.split('?')[0]?.split('#')[0];

                    if (productHandle && !seenProductHandles.has(productHandle)) {
                      card.dataset.recommendedFrom = productId;
                      card.dataset.productHandle = productHandle;
                      allProductCards.push(card.outerHTML);
                      seenProductHandles.add(productHandle);
                    }
                  }
                });
              }
            }
          } catch (error) {
            continue;
          }

          if (allProductCards.length >= limit) {
            break;
          }
        }

        if (allProductCards.length > 0) {
          const currentProductsContainer = this.querySelector('.cart-recommendations__products .product-grid');
          if (currentProductsContainer) {
            const limitedCards = allProductCards.slice(0, limit);
            currentProductsContainer.innerHTML = limitedCards.join('');
            this.showProducts();
            return;
          }
        }

        this.showEmpty();
      } catch (error) {
        this.showEmpty();
      }
    }

    showProducts() {
      const productsEl = this.querySelector('.cart-recommendations__products');
      const emptyEl = this.querySelector('.cart-recommendations__empty');
      const sectionWrapper = this.closest('[class*="color-"][class*="gradient"]');

      if (productsEl) productsEl.style.display = 'block';
      if (emptyEl) emptyEl.style.display = 'none';

      if (sectionWrapper) {
        sectionWrapper.style.display = 'block';
      }
    }

    showEmpty() {
      const productsEl = this.querySelector('.cart-recommendations__products');
      const emptyEl = this.querySelector('.cart-recommendations__empty');
      const sectionWrapper = this.closest('[class*="color-"][class*="gradient"]');

      if (productsEl) productsEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'block';

      if (sectionWrapper) {
        sectionWrapper.style.display = 'none';
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
} 